// src/pages/gift/GiftPage.jsx — public gift card purchase. Anyone, logged in
// or not, can buy; Stripe's hosted Checkout collects the card and the buyer's
// email, and the webhook issues + emails the code.
import React, { useState } from 'react';
import { formatMoney } from '../../lib/formatMoney';

const API = process.env.REACT_APP_API_URL;

const TIERS = [
    { months: 1, amount: 500 },
    { months: 3, amount: 1500 },
    { months: 6, amount: 3000 },
    { months: 12, amount: 6000 },
];

const inputClass = 'w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm placeholder-neutral-600 focus:ring-2 focus:ring-cyan-500/60 focus:outline-none';

const GiftPage = () => {
    const [months, setMonths] = useState(3);
    const [recipientEmail, setRecipientEmail] = useState('');
    const [recipientName, setRecipientName] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!recipientEmail.trim()) {
            setError('Please enter the recipient\'s email address.');
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const res = await fetch(`${API}/api/create_gift_checkout.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    months,
                    recipient_email: recipientEmail.trim(),
                    ...(recipientName.trim() ? { recipient_name: recipientName.trim() } : {}),
                    ...(message.trim() ? { message: message.trim() } : {}),
                }),
            });
            const data = await res.json();
            if (res.ok && data.status === 'success' && data.url) {
                window.location.href = data.url;
                return;
            }
            setError(data.message || 'Could not start the gift purchase. Please try again.');
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="px-6 py-12 text-white">
            <div className="mx-auto max-w-2xl">
                <header className="mb-8 text-center">
                    <h1 className="text-3xl font-bold tracking-tight">Gift Premium</h1>
                    <p className="mt-2 text-sm text-neutral-400">
                        Give someone unlimited AI resume optimizations. We'll email them the code right away.
                    </p>
                </header>

                <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-[#0b0e14]/60 backdrop-blur-sm p-6 space-y-6">
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-400 mb-3">Choose a length</p>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                            {TIERS.map((t) => (
                                <button
                                    key={t.months}
                                    type="button"
                                    onClick={() => setMonths(t.months)}
                                    className={`rounded-xl border p-4 text-center transition-colors ${months === t.months
                                        ? 'border-cyan-500/60 bg-cyan-500/10'
                                        : 'border-white/10 bg-white/[0.04] hover:border-white/25'}`}
                                >
                                    <div className="text-sm font-semibold">{t.months} month{t.months === 1 ? '' : 's'}</div>
                                    <div className="mt-1 text-lg font-bold text-cyan-300">{formatMoney(t.amount, 'usd')}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div>
                            <label className="block text-xs text-neutral-400 mb-1">Recipient email</label>
                            <input className={inputClass} placeholder="recipient email" value={recipientEmail}
                                onChange={(e) => setRecipientEmail(e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-xs text-neutral-400 mb-1">Their name (optional)</label>
                            <input className={inputClass} placeholder="their name" value={recipientName}
                                onChange={(e) => setRecipientName(e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-xs text-neutral-400 mb-1">Message (optional)</label>
                            <textarea className={`${inputClass} resize-none`} rows="3" placeholder="add a short note"
                                value={message} onChange={(e) => setMessage(e.target.value)} />
                        </div>
                    </div>

                    {error && <p className="text-red-400 text-sm">{error}</p>}

                    <button type="submit" disabled={busy}
                        className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-3 rounded-lg disabled:opacity-50 transition-colors">
                        {busy ? 'Starting…' : 'Continue to payment'}
                    </button>
                    <p className="text-center text-xs text-neutral-500">
                        Payment is handled by Stripe. The code is emailed to your recipient as soon as it clears.
                    </p>
                </form>
            </div>
        </div>
    );
};

export default GiftPage;
